import { useEffect, useState } from 'react';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import styles from './Login.module.css';
import LogoIcon from '../../icons/LogoIcon';
import Button from '../../components/Button/Button';
import { api, setToken } from '../../lib/api';

// How long "Resend code" stays locked after a send. Email is quick, but an
// unlocked button invites the repeat taps that mail-bomb the inbox.
const RESEND_COOLDOWN_S = 60;

// Typing a password blind is where most failed sign-ins come from — the eye is
// the fix, and both the login and reset forms need it.
const PasswordField = ({ id, value, onChange, placeholder, autoComplete }) => {
    const [visible, setVisible] = useState(false);
    return (
        <div className={styles.passwordWrap}>
            <input
                id={id}
                type={visible ? 'text' : 'password'}
                className={`${styles.input} ${styles.passwordInput} w-100`}
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                autoComplete={autoComplete}
            />
            <button
                type="button"
                className={styles.passwordToggle}
                onClick={() => setVisible((prev) => !prev)}
                // Names the action the tap performs, not the current state — the
                // reader is choosing what to do, not reading a status.
                aria-label={visible ? 'Hide password' : 'Show password'}
            >
                {visible ? <FiEyeOff size={18} /> : <FiEye size={18} />}
            </button>
        </div>
    );
};

const Login = () => {
    const navigate = useNavigate();
    // 'login' collects email + password. 'reset' is the forgot-password step:
    // the code we mailed, typed back alongside the new password in one submit.
    const [view, setView] = useState('login');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [code, setCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState(null);
    const [error, setError] = useState(null);
    const [lastSentAt, setLastSentAt] = useState(null);

    // Seconds until "Resend code" unlocks, counted from when the code was sent.
    const [cooldown, setCooldown] = useState(0);
    useEffect(() => {
        if (!lastSentAt) return undefined;
        const secondsLeft = () =>
            Math.max(0, RESEND_COOLDOWN_S - Math.floor((Date.now() - lastSentAt) / 1000));
        setCooldown(secondsLeft());
        const id = setInterval(() => {
            const left = secondsLeft();
            setCooldown(left);
            if (left <= 0) clearInterval(id);
        }, 1000);
        return () => clearInterval(id);
    }, [lastSentAt]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setBusy(true);
        setError(null);
        try {
            const { token } = await api('/auth/admin/login', {
                method: 'POST',
                body: { email: email.trim(), password },
                auth: false,
            });
            setToken(token);
            navigate('/dashboard');
        } catch (err) {
            setError(err.message);
            setBusy(false);
        }
    };

    // Doubles as the resend handler. The API deliberately answers the same way
    // whether or not the address belongs to an admin, so the wording here can't
    // promise a code actually went anywhere.
    const requestReset = async () => {
        const address = email.trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) {
            setError('Enter your email address first');
            return;
        }
        setBusy(true);
        setError(null);
        try {
            await api('/auth/admin/request-reset', {
                method: 'POST',
                body: { email: address },
                auth: false,
            });
            setLastSentAt(Date.now());
            setNotice(`If ${address} belongs to an admin, a reset code is on its way.`);
            if (view !== 'reset') {
                setCode('');
                // The box on the next screen is for the NEW password — carrying
                // over the one that just failed pre-fills a wrong answer.
                setPassword('');
                setView('reset');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const handleReset = async (event) => {
        event.preventDefault();
        setBusy(true);
        setError(null);
        try {
            // Resolves with a session — proving the mailbox and choosing the
            // password IS the sign-in, so there is no login form to return to.
            const { token } = await api('/auth/admin/reset', {
                method: 'POST',
                body: { email: email.trim(), code: code.trim(), password },
                auth: false,
            });
            setToken(token);
            navigate('/dashboard');
        } catch (err) {
            setError(err.message);
            setBusy(false);
        }
    };

    const backToLogin = () => {
        setView('login');
        setCode('');
        setPassword('');
        setNotice(null);
        setError(null);
    };

    const resendLabel = cooldown > 0
        ? `Resend code in ${Math.floor(cooldown / 60)}:${String(cooldown % 60).padStart(2, '0')}`
        : 'Resend code';

    return (
        <div className={`${styles.page} min-vh-100 d-flex align-items-center justify-content-center p-3`}>
            <div className={`${styles.card} w-100`}>
                <div className={styles.logo}>
                    <LogoIcon size={48} />
                </div>

                <h1 className={`${styles.title} mt-4 mb-4`}>
                    {view === 'reset' ? 'Reset your password' : 'Log into your account'}
                </h1>

                {view === 'login' ? (
                    <form onSubmit={handleSubmit}>
                        <div className="mb-4">
                            <label htmlFor="email" className={`${styles.label} mb-2`}>Email</label>
                            <input
                                id="email"
                                type="email"
                                className={`${styles.input} w-100`}
                                placeholder="Enter email"
                                value={email}
                                onChange={(event) => setEmail(event.target.value)}
                                autoComplete="username"
                            />
                        </div>

                        <div className="mb-2">
                            <label htmlFor="password" className={`${styles.label} mb-2`}>Password</label>
                            <PasswordField
                                id="password"
                                placeholder="Enter password"
                                value={password}
                                onChange={(event) => setPassword(event.target.value)}
                                autoComplete="current-password"
                            />
                        </div>

                        <div className={`${styles.forgotRow} mb-4`}>
                            <button
                                type="button"
                                className={styles.linkBtn}
                                disabled={busy}
                                onClick={requestReset}
                            >
                                Forgot password?
                            </button>
                        </div>

                        {error && <div className="text-danger mb-3" style={{ fontSize: '0.9rem' }}>{error}</div>}

                        <Button type="submit" className="w-100 mt-2" disabled={busy}>
                            {busy ? 'Logging in…' : 'Login'}
                        </Button>
                    </form>
                ) : (
                    <form onSubmit={handleReset}>
                        {notice && <div className={`${styles.notice} mb-4`}>{notice}</div>}

                        <div className="mb-4">
                            <label htmlFor="reset-code" className={`${styles.label} mb-2`}>Code</label>
                            <input
                                id="reset-code"
                                type="text"
                                inputMode="numeric"
                                maxLength={6}
                                className={`${styles.input} w-100`}
                                placeholder="6-digit code"
                                value={code}
                                onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
                                autoComplete="one-time-code"
                            />
                        </div>

                        <div className="mb-4">
                            <label htmlFor="reset-password" className={`${styles.label} mb-2`}>New password</label>
                            <PasswordField
                                id="reset-password"
                                placeholder="At least 8 characters"
                                value={password}
                                onChange={(event) => setPassword(event.target.value)}
                                autoComplete="new-password"
                            />
                        </div>

                        {error && <div className="text-danger mb-3" style={{ fontSize: '0.9rem' }}>{error}</div>}

                        <Button
                            type="submit"
                            className="w-100 mt-2"
                            disabled={busy || code.length !== 6 || password.length < 8}
                        >
                            {busy ? 'Resetting…' : 'Reset password'}
                        </Button>

                        <div className={`${styles.linkRow} mt-3`}>
                            <button
                                type="button"
                                className={styles.linkBtn}
                                disabled={busy || cooldown > 0}
                                onClick={requestReset}
                            >
                                {resendLabel}
                            </button>
                            <button
                                type="button"
                                className={styles.linkBtn}
                                disabled={busy}
                                onClick={backToLogin}
                            >
                                Back to login
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default Login;
